// A build on the product flow (Mitch: "for a make plan, we can see that same visual, along with little status indicators
// that … communicate what parts are where in the process, what is done, estimate dates"): pick one of the item's builds
// (an open make PO, or the plan's next build) and each part HHL supplies gets a bar and a line from the Make plan's own
// readiness (shared/make.ts): at the CM, on its way there, at HHL to ship, on order to HHL, short, each with its date; the
// build's card has its stage, units built and dates. Parts the maker makes or orders are the maker's to do: no bar.
import { CheckCircle2 } from "lucide-react";
import { type Ref, useEffect } from "react";
import { MAKE_STAGE_LABELS, MAKE_STAGES } from "../../../../shared/consignment.ts";
import type { MakeDashboard, Readiness, ReadinessLine } from "../../../../shared/make.ts";
import { fmt, type Plan, planDayText } from "../../../../shared/planning.ts";
import { useMakeDashboard } from "../../lib/make.ts";
import { Link } from "../../lib/router.tsx";
import { Badge, cx } from "../../ui/controls.tsx";
import { ShipFromHhl } from "../consignment/Shipments.tsx";

const EPS = 1e-9;

/** Works out the Make plan (the first plan takes about 0.5 s), so the page mounts it after its first paint. */
export function MakeLoader({ onLoad }: { onLoad: (m: { plan: Plan; dash: MakeDashboard }) => void }) {
  const { plan, dash } = useMakeDashboard();
  useEffect(() => onLoad({ plan, dash }), [plan, dash, onLoad]);
  return null;
}

/** "PO-1024 · 380 to build", "Next build · 500 (planned)". */
export const buildLabel = (rd: Readiness) =>
  rd.build.poNumber
    ? `${rd.build.poNumber}${rd.build.draft ? " (draft)" : ""} · ${fmt(Math.round(rd.build.qty))} to build`
    : `Next build · ${fmt(Math.round(rd.build.qty))} (planned)`;

/** Where a part is for the build, worst first, and the bar's segments (each a share of what the build needs). */
const PARTS = [
  { key: "atCm", color: "var(--ok)", label: (place: string) => `At ${place}` },
  { key: "toCm", color: "var(--series-1)", label: () => "On its way there" },
  { key: "atHhl", color: "var(--series-2)", label: () => "At HHL: ship it" },
  { key: "onOrder", color: "color-mix(in srgb, var(--series-1) 40%, transparent)", label: () => "On order to HHL" },
  { key: "short", color: "var(--danger)", label: () => "Short" },
] as const;

/** A part's state in one line ("200 short · Nov 20", "On its way · Nov 12", "✓ At Elecrow") and its colour. */
export function partText(l: ReadinessLine, today: string, place: string): { text: string; tone: string } {
  const day = (d: number | null) => planDayText(d, today);
  if (l.short > EPS)
    return { text: `${fmt(Math.ceil(l.short))} short${l.shortEta === null ? " · not ordered" : ` · ${day(l.shortEta)}`}`, tone: "text-danger" };
  if (l.onOrder > EPS) return { text: `On order · ${day(l.onOrderEta)}`, tone: "text-[var(--series-1)]" };
  if (l.atHhl > EPS) return { text: `Ship ${fmt(Math.ceil(l.atHhl))} from HHL`, tone: "text-[var(--series-2)]" };
  if (l.toCm > EPS) return { text: `On its way · ${day(l.toCmEta)}`, tone: "text-[var(--series-1)]" };
  return { text: `✓ At ${place}`, tone: "text-ok" };
}

/** The tooltip's words: "Needs 1,000: 600 at Elecrow · 200 on its way (Nov 12) · 200 short (could be there Nov 20)". */
export function partTitle(l: ReadinessLine, today: string, place: string): string {
  const day = (d: number | null) => planDayText(d, today);
  const n = (q: number) => fmt(Math.round(q));
  const bits = [
    l.atCm > EPS && `${n(l.atCm)} at ${place}`,
    l.toCm > EPS && `${n(l.toCm)} on its way (${day(l.toCmEta)})`,
    l.atHhl > EPS && `${n(l.atHhl)} at HHL to ship`,
    l.onOrder > EPS && `${n(l.onOrder)} on order to HHL (${day(l.onOrderEta)})`,
    l.short > EPS && `${fmt(Math.ceil(l.short))} short (${l.shortEta === null ? "not ordered" : `could be there ${day(l.shortEta)}`})`,
  ].filter(Boolean);
  return `Needs ${n(l.need)} for this build: ${bits.join(" · ") || "nothing"}`;
}

/** The bar along a box's bottom: its share at the CM, on its way, at HHL, on order and short. */
export function PartBar({ l, height }: { l: ReadinessLine; height: number }) {
  if (l.need <= EPS) return null;
  return (
    <div className="absolute inset-x-0 bottom-0 flex overflow-hidden rounded-b-[7px]" style={{ height }} aria-hidden>
      {PARTS.map((p) => (l[p.key] > EPS ? <span key={p.key} style={{ flexGrow: l[p.key], background: p.color }} /> : null))}
    </div>
  );
}

/** The product's own bar: units built of the build's. */
export function BuiltBar({ built, of, height }: { built: number; of: number; height: number }) {
  if (of <= 0) return null;
  return (
    <div className="absolute inset-x-0 bottom-0 overflow-hidden rounded-b-[7px] bg-line" style={{ height }} aria-hidden>
      <div className="h-full bg-ok" style={{ width: `${Math.min(100, (100 * built) / of)}%` }} />
    </div>
  );
}

/**
 * The build, over the canvas's bottom left (beside the product, where a tree is narrowest; the top on a phone): what it
 * is, its stage, units built, dates, and whether HHL's parts are there.
 */
export function BuildCard({
  ref,
  rd,
  today,
  place,
  site,
  itemId,
}: {
  ref: Ref<HTMLDivElement>;
  rd: Readiness;
  today: string;
  place: string;
  site: string | null;
  itemId: string;
}) {
  const b = rd.build;
  const mo = b.makeOrder;
  const day = (d: number | null) => planDayText(d, today);
  const stage = mo ? MAKE_STAGES.indexOf(mo.mo.stage) : -1;
  const g = rd.gating;
  return (
    <div
      ref={ref}
      className="absolute bottom-3 left-3 flex w-[min(380px,calc(100%-24px))] max-sm:top-3 max-sm:bottom-auto cursor-default flex-col gap-2 rounded-lg border border-line bg-surface p-3 text-[12px] shadow-sm"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold">{b.poNumber ?? "Next build"}</span>
        {b.draft && <Badge>Draft</Badge>}
        {!b.poNumber && <Badge tone="accent">Planned</Badge>}
        <span className="ml-auto text-muted">
          {fmt(Math.round(mo?.qty ?? b.qty))} units · {place}
        </span>
      </div>
      {mo && (
        <ol className="flex items-start" aria-label={`Stage: ${MAKE_STAGE_LABELS[mo.mo.stage]}`}>
          {MAKE_STAGES.map((s, i) => (
            <li key={s} className="relative flex flex-1 flex-col items-center gap-1">
              {i > 0 && <span className={cx("absolute top-[5px] right-1/2 h-0.5 w-full", i <= stage ? "bg-ok" : "bg-line")} />}
              <span
                className={cx(
                  "relative size-3 rounded-full border-2",
                  i < stage ? "border-ok bg-ok" : i === stage ? "border-accent bg-accent-soft ring-2 ring-accent/25" : "border-line-strong bg-surface",
                )}
              />
              <span className={cx("text-center text-[10px] leading-tight", i === stage ? "font-semibold text-text" : "text-faint")}>
                {s === "materials_ready" ? "Parts in" : MAKE_STAGE_LABELS[s]}
              </span>
            </li>
          ))}
        </ol>
      )}
      {mo && (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-ok" style={{ width: `${mo.qty ? Math.min(100, (100 * mo.output) / mo.qty) : 0}%` }} />
          </div>
          <span className="text-muted tabular-nums">
            {fmt(mo.output)} of {fmt(mo.qty)} built
          </span>
        </div>
      )}
      <div className="text-muted">{b.poNumber ? `Starts ${day(b.start)} · due ${day(b.end)}` : `Order it by ${day(b.start)} · arrives ${day(b.end)}`}</div>
      <div>
        {rd.lines.length === 0 ? (
          <span className="text-muted">HHL sends nothing for it: {place} gets every part.</span>
        ) : rd.ready && rd.toShip ? (
          <ShipFromHhl
            site={site}
            lines={rd.lines.filter((l) => l.atHhl > 0).map((l) => ({ itemId: l.itemId, qty: l.atHhl }))}
            label={`Ship ${fmt(rd.toShip)} from HHL${rd.readyDay ? ` by ${day(rd.readyDay)}` : ""}`}
            className="font-medium text-[var(--series-2)] hover:underline"
          />
        ) : rd.ready ? (
          <span className="flex items-center gap-1 font-medium text-ok">
            <CheckCircle2 className="size-3.5" />
            {rd.readyDay ? `HHL's parts there by ${day(rd.readyDay)}` : `HHL's parts all at ${place}`}
          </span>
        ) : (
          <span className={cx("font-medium", rd.readyDay === null || rd.readyDay > Math.max(0, b.start) ? "text-danger" : "text-warn")}>
            {rd.shortParts ? `${rd.shortParts} part${rd.shortParts === 1 ? "" : "s"} short` : "Waiting on parts"}
            {g && (
              <span className="font-normal text-muted">
                {" "}
                · last is {g.item?.sku ?? "a part"}, {g.readyDay === null ? "not ordered" : `by ${day(g.readyDay)}`}
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 text-[11px] text-muted">
        {PARTS.map((p) => (
          <span key={p.key} className="flex items-center gap-1">
            <span className="h-1.5 w-3 rounded-full" style={{ background: p.color }} />
            {p.label(place)}
          </span>
        ))}
        <Link to={`/make?item=${itemId}`} className="ml-auto text-accent-text hover:underline">
          Make plan
        </Link>
      </div>
    </div>
  );
}
